/* eslint-disable */
import { ProctoringRoute, getEndpoint } from "routes/route";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import Button from "components/Button";
import FooterLogo from "components/FooterLogo";
import Header from "components/Header";
import LoaderGreen from "assets/img/loader_green.gif";
import Message from "components/Message";
import Canvas from "components/Canvas";
import VideoPlayer from "components/VideoPlayer";
import { alertHelper } from "helpers/alert-helper";
import { AlertName } from "helpers/alert-type";
import { useAppDispatch, useAppSelector } from "store/store";
import { useFaceCapture, faceCaptureUploadApi } from "services/faceCaptureUpload";
import { GlobalAppState } from "globals/interfaces";
import { nextScreen } from "routes/workflow";
import { AlertLogConfig } from "services/session-token";
import Base from "components/layouts/Base";
import Content from "components/layouts/Content";
import { MediaContext } from "context/MediaContext";
import { hideProview } from "../helpers/hideProview";
import { FaceDetector, FilesetResolver, ObjectDetector } from "@mediapipe/tasks-vision";
import { ErrorMessage, ScreenShareStatus, SessionScope } from "globals/enums";
import { isWebGLSupported } from "helpers/face-detection";
import {
  faceDetectionModelUrl,
  mediapipeWasmUrl,
  objectDetectionModelUrl,
  objectDetectionPersonScoreThreshold,
  orgIdsWithFaceDetection
} from "config/constant";
import { parseOrgIds } from "helpers/common";
import { guardedStepNavigation } from "helpers/guarded-step-navigation";
import { isFaceDetectionEligible } from "helpers/face-detection-eligibility";
import { handleError } from "../helpers/sentry";

function FaceCapture(): React.ReactElement | null {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const globalState: GlobalAppState = useAppSelector((state) => state.app);
  const screenShareStatus = useAppSelector((state) => state.screenShare.screenShareStatus);
  const screenShareStatusRef = useRef<ScreenShareStatus | "">(screenShareStatus);

  useEffect(() => {
    screenShareStatusRef.current = screenShareStatus;
  }, [screenShareStatus]);

  const [
    faceCapture,
    {
      isUninitialized: createAlertIsUninitialized,
      isLoading: createAlertIsLoading,
      isSuccess: createAlertSuccess
    }
  ] = useFaceCapture();
  const alertConfig: AlertLogConfig[] = useAppSelector((state) => state.app.alertConfigs);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [faceCaptureFailure, setFaceCaptureFailure] = useState<boolean>(false);
  const [captured, setCaptured] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const { mediaStream, mediaStreamError } = useContext(MediaContext);
  const faceDetectorRef = useRef<FaceDetector | null>(null);
  const objectDetectorRef = useRef<ObjectDetector | null>(null);
  const visionRef = useRef<Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>> | null>(null);
  const isActiveRef = useRef(true);

  useEffect(() => {
    isActiveRef.current = true;
    return () => {
      isActiveRef.current = false;
      faceDetectorRef.current?.close();
      faceDetectorRef.current = null;
      objectDetectorRef.current?.close();
      objectDetectorRef.current = null;
      visionRef.current = null;
    };
  }, []);

  const proceedToNextScreen = useCallback(() => {
    if (mediaStreamError && mediaStreamError.name === "AudioHardwareFailedError") {
      hideProview();
      navigate(getEndpoint(ProctoringRoute.RecorderScreen));
      return;
    }

    const getNextScreen = nextScreen(
      ProctoringRoute.FaceCapture,
      globalState.configuration,
      globalState.session.session_type
    );
    guardedStepNavigation({
      sourceRoute: ProctoringRoute.FaceCapture,
      destinationRoute: getNextScreen,
      screenShareStatus: screenShareStatusRef.current,
      dispatch,
      navigate
    });
  }, [
    mediaStreamError,
    globalState.configuration,
    globalState.session.session_type,
    dispatch,
    navigate
  ]);

  const uploadFaceCapture = async (payload: string) => {
    try {
      const alertData = alertHelper(alertConfig).getAlert(AlertName.FaceCapture, payload, null);
      await faceCapture(alertData).unwrap();
      if (!isActiveRef.current) return;
      alertHelper(alertConfig).raiseAlert(AlertName.FaceCaptureSuccess, dispatch);
      setLoading(false);
      proceedToNextScreen();
    } catch (e: unknown) {
      if (!isActiveRef.current) return;
      setLoading(false);
      if (e === ErrorMessage.MultipleFacesDetected || e === ErrorMessage.NoFaceDetected) {
        if (!parseOrgIds(orgIdsWithFaceDetection).includes(globalState.organizationId)) {
          alertHelper(alertConfig).raiseAlert(AlertName.FaceCaptureSuccess, dispatch);
          proceedToNextScreen();
        }
      } else {
        handleError(e instanceof Error ? e : new Error(String(e)));
      }
      setFaceCaptureFailure(true);
      setErrorMessage(typeof e === "string" ? e : "");
      alertHelper(alertConfig).raiseAlert(AlertName.FaceCaptureFailure, dispatch);
    }
  };

  const processFaceCapture = async () => {
    if (!canvasRef.current) return;

    setLoading(true);
    const payload = canvasRef.current?.toDataURL("image/png");
    const sessionType = globalState?.session?.session_type;
    const organizationId = globalState.organizationId;
    const configuredOrganizationIds = parseOrgIds(orgIdsWithFaceDetection);
    const detectionEligible = isFaceDetectionEligible(
      sessionType,
      organizationId,
      configuredOrganizationIds
    );

    try {
      if (!detectionEligible) {
        await uploadFaceCapture(payload);
        return;
      }

      // Object detection runs only within this eligible path; no separate guard needed below.
      if (!visionRef.current) {
        visionRef.current = await FilesetResolver.forVisionTasks(mediapipeWasmUrl);
      }

      const delegate = isWebGLSupported() ? "GPU" : "CPU";

      if (!faceDetectorRef.current) {
        faceDetectorRef.current = await FaceDetector.createFromOptions(visionRef.current, {
          baseOptions: {
            modelAssetPath: faceDetectionModelUrl,
            delegate
          },
          runningMode: "IMAGE",
          minDetectionConfidence: 0.5,
          minSuppressionThreshold: 0.3
        });
      }

      // Capture canvas before any await so TypeScript null-narrowing is preserved.
      const canvas = canvasRef.current;
      if (!canvas) return;

      const facePredictions = faceDetectorRef.current.detect(canvas).detections;
      if (facePredictions.length === 0) {
        setLoading(false);
        setFaceCaptureFailure(true);
        setErrorMessage(ErrorMessage.NoFaceDetected);
        alertHelper(alertConfig).raiseAlert(AlertName.FaceCaptureFailure, dispatch);
      } else if (facePredictions.length > 1) {
        setLoading(false);
        setFaceCaptureFailure(true);
        setErrorMessage(ErrorMessage.MultipleFacesDetected);
        alertHelper(alertConfig).raiseAlert(AlertName.FaceCaptureFailure, dispatch);
      } else {
        // Person verification only applies to the single-face case;
        // 0 or 2+ faces are handled above without object detection.
        if (!objectDetectorRef.current) {
          objectDetectorRef.current = await ObjectDetector.createFromOptions(visionRef.current, {
            baseOptions: {
              modelAssetPath: objectDetectionModelUrl,
              delegate
            },
            runningMode: "IMAGE",
            scoreThreshold: objectDetectionPersonScoreThreshold,
            categoryAllowlist: ["person"]
          });
        }
        const objectResult = objectDetectorRef.current.detect(canvas);
        const isPerson = objectResult.detections.length > 0;
        if (!isPerson) {
          setLoading(false);
          setFaceCaptureFailure(true);
          setErrorMessage(ErrorMessage.NoPersonDetected);
          alertHelper(alertConfig).raiseAlert(AlertName.FaceCaptureFailure, dispatch);
        } else {
          await uploadFaceCapture(payload);
        }
      }
    } catch (error) {
      setLoading(false);
      setFaceCaptureFailure(true);
      handleError(error);
    }
  };

  useEffect(() => {
    if (captured) {
      processFaceCapture();
    }
  }, [captured]);

  const handleReTake = () => {
    dispatch(faceCaptureUploadApi.util.resetApiState());
    setCaptured(false);
    setFaceCaptureFailure(false);
  };

  return (
    <Base>
      <Header
        title={<FormattedMessage id="page.faceCapture.header" />}
        titleClass={"tvp-text-sm tvp-font-semibold tvp-clr-neutral tvp-uppercase tvp-ml-2.5"}
        tabIndex={1}
      />
      <Content>
        <div
          className="tvp-w-full tvp-h-auto tvp-bg-white  tvp-my-4 tvp-flex-grow tvp-flex tvp-flex-col tvp-px-2 tvp-items-center tvp-justify-center"
          role="main"
          aria-live="polite"
        >
          {!mediaStream && <img src={LoaderGreen} alt="Loading..." role="status" />}
          {mediaStream && (
            <>
              <div className="tvp-photIdVideoPlayerWrapper tvp-mb-9">
                <VideoPlayer
                  mediaStream={mediaStream}
                  videoRef={videoRef}
                  playerWidth={"200px"}
                  aria-label="Face capture video player"
                />
                {captured && (
                  <Canvas
                    videoRef={videoRef}
                    canvasRef={canvasRef}
                    height={150}
                    width={200}
                    aria-label="Captured face canvas"
                  />
                )}
              </div>
              {createAlertIsUninitialized && (
                <p className="tvp-mt-4" role="alert">
                  <FormattedMessage id="page.faceCapture.initialize.text" />
                </p>
              )}
              {(createAlertIsLoading || loading) && !faceCaptureFailure && (
                <div
                  className="tvp-flex tvp-flex-row tvp-justify-center tvp-mt-3"
                  aria-live="polite"
                  role="status"
                >
                  <img className="tvp-squre-25 tvp-mr-4" src={LoaderGreen} alt="Loading icon" />
                  <p>
                    <FormattedMessage id="page.faceCapture.loading.text" />
                  </p>
                </div>
              )}
              {createAlertSuccess && !faceCaptureFailure && (
                <div role="alert">
                  <Message
                    messages={<FormattedMessage id="page.faceCapture.success.text" />}
                    className="tvp-w-full tvp-flex"
                  />
                  <div className="tvp-flex tvp-flex-row tvp-justify-center tvp-mt-3">
                    <img className="tvp-squre-25 tvp-mr-4" src={LoaderGreen} alt="LoaderGreen" />
                    <Message
                      messages={<FormattedMessage id="page.faceCapture.proceeding.text" />}
                      className="tvp-flex tvp-justify-center"
                    />
                  </div>
                </div>
              )}
              {faceCaptureFailure &&
                (errorMessage === ErrorMessage.MultipleFacesDetected ? (
                  <Message
                    messages={<FormattedMessage id="error.multiplefacesDetected" />}
                    className="tvp-w-full tvp-flex tvp-clr-red tvp-justify-center"
                  />
                ) : (
                  <Message
                    messages={<FormattedMessage id="error.nofaceDetected" />}
                    className="tvp-w-full tvp-flex tvp-clr-red tvp-justify-center"
                  />
                ))}
              {!faceCaptureFailure ? (
                <Button
                  tabIndex={2}
                  label={<FormattedMessage id="page.faceCapture.takeAPicture.button" />}
                  className={
                    createAlertIsUninitialized
                      ? "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-clr-white tvp-mx-auto tvp-bg-green tvp-bdr-green tvp-min-w-150 tvp-mt-4"
                      : "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-bg-lgray tvp-clr-dgray tvp-mx-auto tvp-bdr-lgray tvp-w-150 tvp-mt-4"
                  }
                  disabled={!createAlertIsUninitialized || loading}
                  onClick={() => setCaptured(true)}
                  aria-label="Take a picture"
                />
              ) : (
                <Button
                  tabIndex={2}
                  label={<FormattedMessage id="page.faceCapture.reTake.button" />}
                  className={
                    "tvp-flex tvp-justify-center tvp-items-center tvp-py-1 tvp-px-5 tvp-rounded tvp-w-auto tvp-border-solid tvp-border-2 tvp-min-w-150 tvp-cursor-pointer tvp-clr-white tvp-mx-auto tvp-bg-red tvp-bdr-red tvp-w-150  tvp-mt-4"
                  }
                  onClick={handleReTake}
                  aria-label="Retake the picture"
                />
              )}
              {faceCaptureFailure && (
                <div className="tvp-flex tvp-flex-col tvp-align-middle tvp-mt-2" role="alert">
                  <p className="tvp-flex tvp-justify-center">
                    <FormattedMessage id="page.faceCapture.or.text" />
                  </p>
                  <p>
                    <span
                      className="tvp-link"
                      onClick={() => navigate(getEndpoint(ProctoringRoute.FaceCaptureInstructions))}
                      role="link"
                    >
                      <FormattedMessage id="page.faceCapture.link" />
                    </span>{" "}
                    <FormattedMessage id="page.faceCapture.link.text" />
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Content>
      <FooterLogo />
    </Base>
  );
}

export default FaceCapture;
